import React, { useEffect, useState, MouseEvent, memo, useMemo } from 'react'
import classnames from 'classnames'

import * as styles from './kitItemSelector.module.scss'
import { Item, ToolTypes, Severity } from '../../../../types'
import {
   IconButton,
   LoadingOverlay,
   Search,
   SnackBar,
   TableWrapper,
} from '../..'
import { useItemGetItemsForKit, useSettings } from '../../../../hooks'
import useItemKitRemove from '../../../../hooks/useItemKit/useItemKitRemove'
import { WarningModal } from '../../warningModal/warningModal'
import useItemKitAdd from '../../../../hooks/useItemKit/useItemKitAdd'
import { ViewName } from './constants'

const KitItemSelector = ({ kitId, itemForm }: KitItemSelectorProps) => {
   const { settings } = useSettings()

   const {
      itemsForKitList,
      isLoadingItems,
      refreshItemsForKitList,
      paginateKitItems,
      isRefetchingItemsForKit,
      itemsForAddToKitList,
      itemsForAddToKitListTotal,
      refreshItemsForAddToKit,
      getAddToKitItems,
      isRefetchingItemsForAddToKIt,
      isLoadingItemsForAddToKit,
   } = useItemGetItemsForKit()

   const { removeFromKit, isRemoveFromKitSuccess, resetRemoveSuccess } =
      useItemKitRemove()

   const {
      addToKit,
      isAddKitSuccess,
      resetAddSuccess,
      error: addToKitError,
   } = useItemKitAdd()

   const [searchQuery, setSearchQuery] = useState<string | null>(null)
   const [itemToDelete, setItemToDelete] = useState<number | null>(null)
   const [isSnackbarVisible, setIsSnackbarVisible] = useState<boolean>(false)

   const [currentSkip, setCurrentSkip] = useState<number>(0)
   const [currentTake, setCurrentTake] = useState<number>(10)
   const [selectedItemQuantity, setSelectedItemQuantity] = useState<string>('1')
   const [isDeletePrompt, setIsDeletePrompt] = useState<boolean>(false)
   const [selectedView, setSelectedView] = useState<ViewName>(
      ViewName.KIT_ITEMS
   )

   const [pagedTableData, setPagedTableData] = useState<[]>([])

   const defaultAddItemListQuery = useMemo(
      () => ({
         defaultLocationID: itemForm.Warehouse?.WarehouseLocationId,
         status: itemForm.Status,
         assignedToUserID: itemForm.AssignedToUserID,
         requestedLocationId: itemForm.RequestedLocationId,
         locationID: itemForm.Warehouse?.CurrentLocationId,
         skip: null,
         take: 10,
         query: null,
      }),
      [itemForm]
   )

   useEffect(() => {
      if (itemsForAddToKitList && searchQuery !== null) {
         refreshItemsForAddToKit()
      }
   }, [searchQuery])

   useEffect(() => {
      if (isRemoveFromKitSuccess !== null) {
         setIsSnackbarVisible(true)
      }
      // if success refresh tables
      if (isRemoveFromKitSuccess) {
         refreshItemsForKitList()
         refreshItemsForAddToKit()
      }
   }, [isRemoveFromKitSuccess])

   useEffect(() => {
      if (isAddKitSuccess) {
         setIsSnackbarVisible(true)
         refreshItemsForAddToKit()
         refreshItemsForKitList()
         setSelectedItemQuantity('1')
      }
   }, [isAddKitSuccess])

   useEffect(() => {
      if (addToKitError !== null) {
         setIsSnackbarVisible(true)
      }
   }, [addToKitError])

   useEffect(() => {
      if (kitId) {
         // get full list for client side pagination items already in kit
         paginateKitItems({
            skip: null,
            take: null,
            query: null,
            kitID: kitId,
         })
      }

      // get full list of items to add to kit
      getAddToKitItems(defaultAddItemListQuery)
   }, [])

   // manage kit slice array from paged data on initial render
   useEffect(() => {
      if (itemsForKitList) {
         handleGetItems('', currentSkip, currentTake)
      }
   }, [itemsForKitList])

   const showRemovePrompt = (kitPrimeKey: number) => {
      setItemToDelete(kitPrimeKey)
      setIsDeletePrompt(true)
   }

   const handleRemoveFromKit = () => {
      removeFromKit(itemToDelete)
      setIsDeletePrompt(false)
   }

   const handleAddItemToKit = (selectedItem: Item) => {
      const itemDetails = {
         KitId: kitId,
         KitItems: [
            {
               kitItemId: selectedItem?.Id,
               Quantity: selectedItemQuantity ?? '1',
               ToolLoanId: selectedItem?.ToolLoanId,
               ToolWarehouseID: selectedItem?.Warehouse?.ToolWarehouseID,
            },
         ],
      }
      addToKit(itemDetails)
   }

   const handleAddItemQuantity = (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedItemQuantity(e.target.value)
   }

   const snackbarMessage = useMemo(() => {
      if (isRemoveFromKitSuccess) return 'The Item was removed from the Kit'
      if (isRemoveFromKitSuccess === false)
         return 'Error removing the Item from the Kit'
      if (isAddKitSuccess && !addToKitError)
         return 'The Item was added to the Kit'
      if (addToKitError !== null) return 'Error adding the Item to the Kit'
   }, [isRemoveFromKitSuccess, isAddKitSuccess, addToKitError])

   const snackbarSeverity = useMemo(() => {
      if (addToKitError !== null) return Severity.ERROR

      if (isRemoveFromKitSuccess || isAddKitSuccess) return Severity.SUCCESS
   }, [isRemoveFromKitSuccess, isAddKitSuccess, addToKitError])

   const closeAndResetSnackBar = () => {
      setIsSnackbarVisible(false)
      resetRemoveSuccess()
      resetAddSuccess()
   }

   const kitItemColumns = [
      {
         Header: 'Action',
         accessor: 'Id',
         maxWidth: 100,
         minWidth: 30,
         align: 'center',
         Cell: ({ row }) => {
            const rowItem = row?.original as Item

            return (
               <IconButton
                  id="delete"
                  tooltip="Remove From Kit"
                  action={() => showRemovePrompt(rowItem?.KitPrimeKey)}
               />
            )
         },
      },

      {
         Header: 'Title',
         accessor: 'Title',
         minWidth: 250,
      },
      {
         Header: 'Type',
         accessor: 'ToolType',
         id: 'ToolType',
         minWidth: 150,
         Cell: ({ row }) => ToolTypes[row.original.ToolType] ?? 'Unknown',
      },
      {
         Header: 'Kit Quantity',
         accessor: 'KitQuantity',
         id: 'KitQuantity',
         minWidth: 250,
      },
      {
         Header: 'Manufacturer',
         accessor: 'Manufacturer',
         id: 'Manufacturer',
         minWidth: 250,
      },
      {
         Header: 'Model No.',
         accessor: 'ModelNumber',
         id: 'ModelNumber',
         minWidth: 250,
      },
      {
         Header: 'Serial No.',
         accessor: 'SerialNumber',
         id: 'SerialNumber',
         minWidth: 250,
      },
      {
         Header: 'Barcode',
         accessor: 'Barcode',
         id: 'Barcode',
      },
      {
         Header: 'Status',
         accessor: 'StatusDesc',
         id: 'Status',
      },
      {
         Header: 'Loaned/Pending To',
         accessor: (row) => {
            if (row.AssignedToUser === null) {
               return ''
            }
            return row.AssignedToUser
         },
         id: 'AssignedToUser',
         minWidth: 250,
      },
      ...(settings?.EnableLocationTracking
         ? [
              {
                 Header: 'Home Warehouse',
                 accessor: 'DefaultLocation',
                 id: 'DefaultLocation',
                 minWidth: 250,
              },
              {
                 Header: 'Current/Pending Location',
                 accessor: 'CurrentLocation',
                 id: 'CurrentLocation',
                 minWidth: 250,
              },
           ]
         : []),
   ]

   const addToKitColumns = [
      {
         Header: 'Action',
         accessor: 'Id',
         maxWidth: 100,
         minWidth: 30,
         align: 'center',
         Cell: ({ row }) => {
            const rowItem = row?.original as Item

            return (
               <IconButton
                  id="add-to-kit"
                  tooltip="Add To Kit"
                  action={() => handleAddItemToKit(rowItem)}
               />
            )
         },
      },

      {
         Header: 'Select Quantity',
         accessor: 'Quantity',
         minWidth: 150,
         maxWidth: 150,
         Cell: ({ row }) => {
            const rowItem = row?.original as Item
            return (
               <select
                  style={{ width: '100%' }}
                  onChange={(e) => handleAddItemQuantity(e)}
                  value={selectedItemQuantity}
               >
                  {rowItem.ToolType == ToolTypes['Standard Item'] && (
                     <option value={1}>1</option>
                  )}
                  {rowItem.ToolType == ToolTypes['Quantity Item'] &&
                     Array.from(Array(rowItem.StatusQuantity)).map((q, i) => (
                        
                        <option key={i} value={i + 1}>
                           {i + 1}
                        </option>
                     ))}
               </select>
            )
         },
      },
      {
         Header: 'Available Quantity',
         accessor: 'StatusQuantity',
         id: 'AvailableQuantity',
         minWidth: 150,
      },
      {
         Header: 'Title',
         accessor: 'Title',
         id: 'Title',
         minWidth: 250,
      },
      {
         Header: 'Description',
         accessor: 'Description',
         id: 'Description',
         minWidth: 250,
      },
      {
         Header: 'Type',
         accessor: 'ToolTypeDesc',
         id: 'Type',
      },
      {
         Header: 'Total Quantity',
         accessor: 'TotalQuantity',
         id: 'TotalQuantity',
      },
      {
         Header: 'Manufacturer',
         accessor: 'Manufacturer',
         id: 'Manufacturer',
      },
      {
         Header: 'Model No.',
         accessor: 'ModelNumber',
         id: 'ModelNumber',
      },
      {
         Header: 'Serial No.',
         accessor: 'SerialNumber',
         id: 'SerialNumber',
      },
      {
         Header: 'Barcode',
         accessor: 'Barcode',
         id: 'Barcode',
      },
      {
         Header: 'Status',
         accessor: 'StatusDesc',
         id: 'Status',
      },
      {
         Header: 'Loaned/Pending To',
         accessor: (row) => {
            if (row.AssignedToUser === null) {
               return ''
            }
            return row.AssignedToUser
         },
         id: 'AssignedToUser',
         minWidth: 250,
      },
      ...(settings?.EnableLocationTracking
         ? [
              {
                 Header: 'Home Warehouse',
                 accessor: 'DefaultLocation',
                 id: 'DefaultLocation',
                 minWidth: 250,
              },
              {
                 Header: 'Current/Pending Location',
                 accessor: 'CurrentLocation',
                 id: 'CurrentLocation',
                 minWidth: 250,
              },
           ]
         : []),
   ]

   const handleTabClick = (
      viewName: ViewName,
      event: MouseEvent<HTMLButtonElement>
   ) => {
      event.preventDefault()
      setSelectedView(viewName)
   }

   const handleGetItems = (
      query?: string,
      skip: number = 0,
      take: number = 10
   ) => {
      setCurrentSkip(skip) // client pagination
      setCurrentTake(take) // client pagination

      const pagedData = itemsForKitList?.slice(skip, take + skip)
      setPagedTableData(pagedData)
   }

   const handleGetItemsToAddToKit = (
      query?: string,
      skip?: number,
      take?: number
   ) => {
      getAddToKitItems({
         ...defaultAddItemListQuery,
         query,
         skip,
         take,
      })
   }

   const handleSearch = (
      query?: string,
      skip?: number,
      take?: number,
      filters?: {}
   ) => {
      if (itemsForAddToKitList && query !== null) {
         getAddToKitItems({
            ...defaultAddItemListQuery,
            skip: null,
            take: null,
            query,
         })
      }
   }

   const showLoadingSpinner = useMemo(
      () =>
         isRefetchingItemsForKit ||
         isLoadingItems ||
         isRefetchingItemsForAddToKIt ||
         isLoadingItemsForAddToKit,
      [
         isRefetchingItemsForKit,
         isLoadingItems,
         isRefetchingItemsForAddToKIt,
         isLoadingItemsForAddToKit,
      ]
   )

   return (
      <>
         {showLoadingSpinner && <LoadingOverlay />}
         <div className={styles.tabs}>
            <button
               className={classnames(styles.tab, {
                  [styles.tabActive]: selectedView === ViewName.KIT_ITEMS,
               })}
               onClick={(event) => handleTabClick(ViewName.KIT_ITEMS, event)}
            >
               Kit Items
            </button>
            <button
               className={classnames(styles.tab, {
                  [styles.tabActive]: selectedView === ViewName.ADD_TO_KIT,
               })}
               onClick={(event) => handleTabClick(ViewName.ADD_TO_KIT, event)}
            >
               Add To Kit
            </button>
         </div>

         <div className={styles.itemList}>
            <div
               className={
                  selectedView === ViewName.KIT_ITEMS ? '' : styles.nonActiveTab
               }
            >
               {itemsForKitList && pagedTableData && Array.isArray(pagedTableData) && (
                  <TableWrapper
                     columns={kitItemColumns}
                     data={pagedTableData}
                     getItems={handleGetItems}
                     totalCount={itemsForKitList?.length}
                     extraClassName=""
                     isLoading={isLoadingItems}
                     isPageReset={false}
                     searchCriteria=""
                     setItemId={() => {}}
                     setPageSized={() => null}
                     hideResultsTitle
                  />
               )}
            </div>

            <div
               className={
                  selectedView === ViewName.ADD_TO_KIT
                     ? ''
                     : styles.nonActiveTab
               }
            >
               <div className={styles.searchWrapper}>
                  <div className={styles.searchWrapperSearch}>
                     <Search
                        handleQuery={handleSearch}
                        pageSized={10}
                        placeHolder={''}
                        setSearchCriteria={setSearchQuery}
                     />
                  </div>
               </div>

               {itemsForAddToKitList && Array.isArray(itemsForAddToKitList) && (
                  <TableWrapper
                     columns={addToKitColumns}
                     data={itemsForAddToKitList}
                     getItems={handleGetItemsToAddToKit}
                     totalCount={itemsForAddToKitListTotal} // this is a hack with no total figure coming from back end
                     extraClassName=""
                     isLoading={false}
                     isPageReset={false}
                     searchCriteria={searchQuery}
                     setItemId={() => {}}
                     setPageSized={() => {}}
                     hideResultsTitle
                  />
               )}
            </div>
         </div>
         {isDeletePrompt && (
            <WarningModal
               isModalOpen={isDeletePrompt}
               handleClose={() => setIsDeletePrompt(false)}
               title={'Delete Confirmation'}
               message={'Are you sure you want to remove this item from Kit?'}
               primaryButtonTitle={'Delete'}
               primaryButtonMethod={handleRemoveFromKit}
               primaryButtonVariant={'danger'}
               secondaryButtonTitle={'Cancel'}
               secondaryButtonMethod={() => setIsDeletePrompt(false)}
               secondaryButtonVariant={'tertiary'}
            />
         )}
         {isSnackbarVisible && (
            <SnackBar
               message={snackbarMessage}
               open={isSnackbarVisible}
               onClose={closeAndResetSnackBar}
               severity={snackbarSeverity}
               disableAutoClose={false}
            />
         )}
      </>
   )
}

export default memo(KitItemSelector)
